import { RefObject, useEffect } from 'react';

interface useSearchDropdownListenersProps {
  closeDropdown: () => void;
  dropdownRef: RefObject<HTMLDivElement>;
  inputRef: RefObject<HTMLInputElement>;
  searchTermState: RefObject<string>;
  setIsFocused: (value: boolean) => void;
  searchSuggestVerticalIndex: number | null;
}

/**
 * Custom hook to handle various events for search dropdown closing logic
 */
const useSearchDropdownListeners = ({
  closeDropdown,
  dropdownRef,
  inputRef,
  searchTermState,
  setIsFocused,
  searchSuggestVerticalIndex,
}: useSearchDropdownListenersProps): void => {
  const handleKeyboardKeydown = (event) => {
    if (event.code === 'Escape' || (event.code === 'Tab' && searchSuggestVerticalIndex === null)) {
      closeDropdown();
      return;
    }
    if (event.code === 'Tab') {
      event.preventDefault();
    }
  };
  const handleOutsideClick = (event) => {
    /*
      Removing last recent search in the list results in dropdown.current.contains(event.target) being false event though it contains it
      due to it being removed before this logic kicks in, that is why we do explicit check.
     */
    const clickedOnRecentSearchRemoveBtn =
      event.target.getAttribute('data-remove') ||
      event.target.parentElement?.getAttribute('data-remove') ||
      event.target.parentElement?.parentElement?.getAttribute('data-remove');
    const clickedOnDropdownElement = dropdownRef.current?.contains(event.target);
    const clickedOnInputElement = inputRef.current?.contains(event.target);
    const clickedOnClearBtn =
      event.target.id === 'clear-button' ||
      event.target.parentElement?.id === 'clear-button' ||
      event.target.parentElement?.parentElement?.id === 'clear-button';
    const shouldCloseDropdown =
      (!clickedOnDropdownElement && !clickedOnInputElement && !clickedOnClearBtn && !clickedOnRecentSearchRemoveBtn) ||
      (clickedOnDropdownElement && searchTermState.current);

    if (shouldCloseDropdown) {
      closeDropdown();
    } else {
      setIsFocused(true);
      inputRef.current?.focus();
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);
    inputRef.current?.addEventListener('keydown', handleKeyboardKeydown);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
      inputRef.current?.removeEventListener('keydown', handleKeyboardKeydown);
    };
  }, [searchSuggestVerticalIndex]);
};

export default useSearchDropdownListeners;
